<template>
    <modal-component
        :visible="visible"
        id="add-payment-modal"
        :title="'Make Payment'"
        size="lg"
        @close="$emit('close')"
    >
        <div class="p-3">
            <section>
                <div class="row form-group">
                    <label class="col-3">Client</label>
                    <div class="col-3">
                        <select class="form-control form-control-sm" v-model="patient_id">
                            <option selected disabled hidden :value="null">Choose one</option>
                            <option :value="p.id" v-for="p in patients" data-private="lipsum">{{
                                p.name
                            }}</option>
                        </select>
                    </div>
                </div>

                <div class="row form-group mt-3">
                    <label class="col-3">Payment Method</label>
                    <div class="col-3">
                        <select class="form-control form-control-sm" v-model="payload.payment_type">
                            <!--THIS VALUE IS BEING WATCHED-->
                            <option selected disabled hidden :value="null">Choose one</option>
                            <option v-for="p in paymentOptions" :value="p.value">{{
                                p.title
                            }}</option>
                            <option
                                v-for="op in patientOpenPayments"
                                :value="`prepayment-${op.id}`"
                                >{{
                                    `Payment #${op.id} - ${helpers.formatMoney(op.balance)} balance`
                                }}</option
                            >
                        </select>
                    </div>
                </div>

                <div class="row form-group">
                    <label class="col-3">Payment Details</label>
                    <div class="col-3">
                        <input
                            type="text"
                            placeholder="e.g. Check #12345"
                            class="form-control form-control-sm"
                            v-model="payload.notes"
                        />
                    </div>
                </div>

                <div class="row form-group">
                    <label class="col-3">Payment Amount</label>
                    <div class="col-3 input-group input-group-sm">
                        <div class="input-group-prepend">
                            <div class="input-group-text">$</div>
                        </div>
                        <input
                            :disabled="payload.payment_id"
                            type="text"
                            placeholder="0.00"
                            class="form-control form-control-sm text-right"
                            v-model.number="payload.payment_amount"
                        />
                    </div>
                </div>

                <div v-show="payload.payment_type === 'cc'" class="row form-group">
                    <label class="col-3">Card Information</label>
                    <div class="col-6">
                        <div id="stripe-card-add-payment" class="form-control"></div>
                    </div>
                </div>

                <div class="px-3 pb-3">
                    <div class="d-flex w-100 justify-content-end">
                        <span class="text-danger" v-if="payload.payment_amount"
                            >Remaining amount: {{ helpers.formatMoney(remainingAmount) }}</span
                        >
                        <span v-else="">&nbsp;</span>
                    </div>
                    <div
                        v-if="remainingAmount < 0"
                        class="d-flex w-100 justify-content-end align-items-center"
                    >
                        <i class="fa fa-exclamation-triangle text-warning"></i>
                        <span class="small font-italic"
                            >The total amount assigned to the charges can't be greater than the
                            payment amount.</span
                        >
                    </div>
                    <div
                        v-if="allocatedAmountGreaterThanCharge"
                        class="d-flex w-100 justify-content-end align-items-center"
                    >
                        <i class="fa fa-exclamation-triangle text-warning"></i>
                        <span class="small font-italic"
                            >The amount allocated to a charge can't be greater than its expected
                            amount.</span
                        >
                    </div>
                </div>
            </section>

            <div class="table-responsive border-light">
                <table class="table table-sm table-striped mb-0">
                    <thead class="thead-dark">
                        <tr>
                            <th class="">Charge #</th>
                            <th class="">Date</th>
                            <th class="">Therapist</th>
                            <th class="text-right pr-2">Service hrs.</th>
                            <th class="text-right pr-3">Amount</th>
                            <th class="text-right pr-3">Balance</th>
                            <th class="text-right pr-4">Payment</th>
                        </tr>
                    </thead>
                    <tbody class="">
                        <template v-for="invoice in patientInvoices">
                            <tr
                                v-for="charge in invoice.charges"
                                :class="{ 'bg-has-value': payload.invoicePayments[charge.id] }"
                            >
                                <td class="">{{ charge.id }}</td>
                                <td class="">
                                    {{
                                        invoice.created_at
                                            ? moment(invoice.created_at).format("MM/DD/YYYY")
                                            : "N/A"
                                    }}
                                </td>
                                <td class="">{{ charge.therapist_name }}</td>
                                <td class="text-right">{{ charge.service_hours }}</td>
                                <td class="text-right">
                                    {{ helpers.formatMoney(charge.expected_amount) }}
                                </td>
                                <td class="text-right">
                                    {{
                                        helpers.formatMoney(
                                            charge.expected_amount - charge.paid_amount
                                        )
                                    }}
                                </td>
                                <td class="text-right">
                                    <span class="d-flex justify-content-end align-items-center">
                                        <div class="input-group input-group-sm">
                                            <div class="input-group-prepend">
                                                <div class="input-group-text">$</div>
                                            </div>
                                            <input
                                                type="text"
                                                placeholder="0.00"
                                                class="form-control form-control-sm text-right"
                                                :class="{
                                                    'border-danger':
                                                        payload.invoicePayments[charge.id] >
                                                        charge.expected_amount,
                                                }"
                                                style="max-width: 5rem;"
                                                v-model.number="payload.invoicePayments[charge.id]"
                                            />
                                        </div>
                                    </span>
                                </td>
                            </tr>
                        </template>
                        <tr v-if="!patientInvoices.length">
                            <td colspan="7">
                                <span class="font-italic">No outstanding charges.</span>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr class="bg-white border-top font-weight-bold">
                            <td colspan="3">
                                {{
                                    numberOfChargesPaid
                                        ? `Paying ${numberOfChargesPaid} charge${
                                              numberOfChargesPaid > 1 ? "s" : ""
                                          }.`
                                        : ""
                                }}
                            </td>
                            <td class="text-right pr-2">
                                {{
                                    patientInvoices.reduce((acc, cur) => acc + cur.service_hours, 0)
                                }}
                            </td>
                            <td class="text-right pr-3">
                                {{
                                    helpers.formatMoney(
                                        patientInvoices.reduce(
                                            (acc, cur) => acc + cur.expected_amount,
                                            0
                                        )
                                    )
                                }}
                            </td>
                            <td class="text-right pr-3">
                                {{
                                    helpers.formatMoney(
                                        patientInvoices.reduce(
                                            (acc, cur) => acc + cur.expected_amount,
                                            0
                                        ) -
                                            patientInvoices.reduce(
                                                (acc, cur) => acc + cur.paid_amount,
                                                0
                                            )
                                    )
                                }}
                            </td>
                            <td class="text-right pr-4">
                                {{ helpers.formatMoney(invoicePaymentsSum) }}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            <section>
                <errors :errors="errors"></errors>

                <div v-if="success" class="alert alert-success">
                    <span>Payment processed successfuly.</span>
                </div>

                <div v-if="payload.payment_type === 'cof'" class="d-flex w-100 justify-content-end">
                    <button
                        id="checkout-cof-p"
                        :disabled="!canPay"
                        class="btn btn-primary text-white font-weight-bold"
                        @click="addPayment(null, 'checkout-cof-p')"
                    >
                        {{
                            payload.payment_amount
                                ? `Charge ${helpers.formatMoney(payload.payment_amount)} to`
                                : "Charge to"
                        }}
                        {{ `${helpers._get(patient, "card_brand", "")} ending in ` }}
                        {{ helpers._get(patient, "card_last_four", "") }}
                    </button>
                </div>
                <div v-if="payload.payment_type === 'cc'" class="d-flex w-100 justify-content-end">
                    <button
                        id="checkout-cc-p"
                        :disabled="!canPay"
                        class="btn btn-primary text-white font-weight-bold"
                        @click="checkout('checkout-cc-p')"
                    >
                        {{
                            payload.payment_amount
                                ? `Charge ${helpers.formatMoney(payload.payment_amount)}`
                                : "Charge credit card"
                        }}
                    </button>
                </div>
                <div
                    v-if="!['cof', 'cc'].includes(payload.payment_type)"
                    class="d-flex w-100 justify-content-end"
                >
                    <button
                        :disabled="!canPay"
                        id="offline-payment"
                        class="btn btn-sm btn-primary text-white"
                        @click="addPayment(null, 'offline-payment')"
                    >
                        Make Payment
                    </button>
                </div>
            </section>
        </div>
    </modal-component>
</template>

<script>
import ModalComponent from "../ui/ModalComponent";
import { helpers } from "../../plugins/helpers";
import { errors } from "../../plugins/errors";

export default {
    name: "AddPayment",
    props: ["invoices", "organization", "patients"],
    components: { ModalComponent },
    mixins: [errors],
    data() {
        return {
            helpers,
            visible: false,
            success: false,
            isStripeMounted: false,
            loading: false,
            patient_id: null,
            invoice_id: null,
            payload: this.generatePayload(),
            patientOpenPayments: [],
        };
    },
    watch: {
        "payload.payment_type"(value) {
            if (value && value.includes("prepayment")) {
                const prepayment = this.patientOpenPayments.find(
                    payment => payment.id == value.split("-")[1]
                );
                this.selectPrepayment(prepayment);
            } else {
                this.payload.payment_id = null;
                this.payload.payment_amount = 0;
                if (
                    ["cc"].includes(value) &&
                    !this.isStripeMounted &&
                    typeof elements !== "undefined"
                ) {
                    helpers.mountStripeElement("stripe-card-add-payment");
                    this.isStripeMounted = true;
                }
            }
        },

        patient_id() {
            if (this.patient_id) {
                this.getPatientOpenPayments();
            }
        },

        visible() {
            const target = document.getElementById("offline-payment");
            const spinner = document.getElementById("spinner-checkout-cc-p");
            if (target) {
                if (spinner) {
                    spinner.parentNode.removeChild(spinner);
                    target.innerHTML = "Make Payment";
                    if (spinner.parentNode) spinner.parentNode.removeChild(hidden);
                }
            }
        },
    },

    computed: {
        numberOfChargesPaid() {
            let chargesPaid = 0;
            if (this.patient_id) {
                Object.keys(this.payload.invoicePayments).forEach(k => {
                    if (this.payload.invoicePayments[k] > 0) {
                        chargesPaid++;
                    }
                });
            }
            return chargesPaid;
        },

        patient() {
            if (this.patient_id) {
                return this.patients.find(patient => patient.id === this.patient_id);
            }
        },

        remainingAmount() {
            if (this.payload.payment_amount) {
                return this.payload.payment_amount - this.invoicePaymentsSum;
            }
        },

        paymentOptions() {
            let paymentMethods = [
                { title: "Check", value: "check" },
                { title: "Cash", value: "cash" },
                { title: "Credit card", value: "cc" },
            ];

            if (this.patient) {
                // List COF if patient has card details
                if (this.patient.card_brand && this.patient.card_last_four) {
                    let cardOnFile = ` (${this.patient.card_brand} ${this.patient.card_last_four})`;
                    paymentMethods.push({ title: `Card on file${cardOnFile}`, value: "cof" });
                }
            }

            paymentMethods.push({ title: `Other`, value: "other" });

            return paymentMethods;
        },

        patientInvoices() {
            if (this.invoices && this.patient_id) {
                if (this.invoice_id) {
                    return this.invoices.filter(invoice => invoice.id === this.invoice_id);
                }
                return this.invoices.filter(invoice => invoice.patient.id === this.patient_id);
            }
            return [];
        },

        invoicePaymentsSum() {
            let sum = 0;
            if (this.patient_id) {
                Object.keys(this.payload.invoicePayments).forEach(
                    k => (sum += this.payload.invoicePayments[k])
                );
            }
            return sum;
        },

        allPatientCharges() {
            let charges = [];
            if (this.patientInvoices) {
                this.patientInvoices.forEach(invoice => {
                    charges = [...charges, ...invoice.charges];
                });
            }
            return charges;
        },

        allocatedAmountGreaterThanCharge() {
            return Object.keys(this.payload.invoicePayments).some(key => {
                const charge = this.allPatientCharges.find(c => c.id == key);
                if (charge) {
                    return (
                        this.payload.invoicePayments[key] >
                        charge.expected_amount - charge.paid_amount
                    );
                }
                return false;
            });
        },

        canPay() {
            return (
                this.patient_id &&
                this.payload.payment_type &&
                this.payload.payment_amount &&
                parseFloat(this.invoicePaymentsSum) &&
                !this.allocatedAmountGreaterThanCharge &&
                this.remainingAmount >= 0
            );
        },
    },
    methods: {
        generatePayload() {
            return {
                invoicePayments: {},
                payment_type: null,
                payment_id: null,
                payment_amount: 0,
            };
        },

        selectPrepayment(prepayment) {
            this.$set(this.payload, "payment_id", prepayment.id);
            this.$set(this.payload, "payment_amount", parseFloat(prepayment.balance));
        },

        getPatientOpenPayments() {
            axios
                .get(
                    `/api/organizations/${this.organization.id}/payments?patient_id=${
                        this.patient_id
                    }&open=true`
                )
                .then(response => {
                    this.patientOpenPayments = response.data.data;
                });
        },

        validatePayload(payload) {
            if (this.remainingAmount < 0) {
                this.setErrors({
                    Error: [`The allocated payments can't exceed the total payment amount.`],
                });
                helpers.toggleSpinner(buttonId);
                return false;
            }
            return payload;
        },

        addPayment(token = null, buttonId = null) {
            // Charge value must be the expected amount while only one charge is allowed
            this.setErrors();
            try {
                card.clear(); //clear Stripe element
            } catch (e) {
                console.info(
                    "Could not mount Stripe element. Are the organization's Stripe keys set up properly?"
                );
            }
            helpers.toggleSpinner(buttonId);
            let charges = [];
            Object.keys(this.payload.invoicePayments).forEach(charge_id =>
                charges.push({ charge_id, amount: this.payload.invoicePayments[charge_id] })
            );
            let payload = {
                amount: this.payload.payment_amount,
                type: this.payload.payment_type.includes("-")
                    ? this.payload.payment_type.split("-")[0]
                    : this.payload.payment_type,
                notes: this.payload.notes,
                patient_id: this.patient_id,
                payment_id: this.payload.payment_id || null,
                charges,
            };
            if (token) {
                payload.token = token;
            }
            payload = this.validatePayload(payload);
            payload = helpers.cleanEmptyProperties(payload);
            if (!payload) return;
            axios
                .post(`/api/organizations/${this.organization.id}/pay`, payload)
                .then(r => {
                    this.resetModal();
                    this.$emit("fetch-invoices");
                    helpers.toggleSpinner(buttonId);
                })
                .catch(e => {
                    console.error(e);
                    this.setErrors(_.get(e, "response.data.errors", {}));
                    helpers.toggleSpinner(buttonId);
                });
        },

        checkout(buttonId) {
            this.setErrors();
            helpers.toggleSpinner(buttonId);
            let context = this;
            stripe.createToken(card).then(function(result) {
                if (result.hasOwnProperty("error")) {
                    helpers.toggleSpinner(buttonId);
                    return;
                }
                if (result.hasOwnProperty("token")) {
                    helpers.toggleSpinner(buttonId);
                    context.addPayment(result.token.id, buttonId);
                }
            });
        },

        resetModal() {
            this.patient_id = null;
            this.invoice_id = null;
            this.success = false;
            this.visible = false;
            this.payload = this.generatePayload();
            this.patientOpenPayments = [];
        },
    },
};
</script>

<style scoped>
tbody {
    display: block;
    max-height: 20rem;
    overflow-y: scroll;
}
thead,
tbody tr,
tfoot {
    display: table;
    width: 100%;
    table-layout: fixed;
}
.bg-has-value {
    background-color: lightyellow !important;
}
</style>
